import { useState } from "react";
import PageLayout from "../../components/PageLayout";
import { getBranchDetails } from "../../lib/hooks";
import { useResponsive } from "../../lib/useResponsive";

import dhronocharyaImg from '../../images/dhronocharya.png';
import kaivalyaImg from '../../images/kaivalya.png'; 
import chittranjanImg from '../../images/chittranjan.png'; 
import nidhiImg from '../../images/nidhi.png';


interface BranchRawRow {
  Branch: string;
  Country: string;
  Officer_Name: string;
  Role: string;
}

interface OfficerInfo {
  name: string;
  role: string;
}

interface GroupedBranch {
  flag: string;
  location: string;
  officers: OfficerInfo[];
}

function getCountryFlag(country: string): string {
  const c = country.trim().toLowerCase();
  if (c === "india") return "🇮🇳";
  if (c === "usa" || c === "united states") return "🇺🇸";
  if (c === "uae" || c === "united arab emirates") return "🇦🇪";
  return "🏳️";
}

const cSuite = [
  { name: "Sandeep Patel", role: "Chief Information & Human Capital" },
  { name: "Kaushal Patel", role: "Chief Strategy & Compliance" },
  { name: "Hardik Padsala", role: "Chief Financial Officer" },
  { name: "Kaivalya Patel", role: "Chief Investment Officer" },
  { name: "Ram Panguluri", role: "Chief Technology Officer" },
  { name: "Divyang Patel", role: "Chief Risk Officer" },
];

export default function LeadershipPage() {
  const { data: rawRows, loading } = getBranchDetails() as {
    data: BranchRawRow[] | null;
    loading: boolean;
  };
  const [activeBranch, setActiveBranch] = useState<string | null>(null);
  const { isMobile } = useResponsive();

  const groupedMap: Record<string, GroupedBranch> = {};
  if (rawRows && Array.isArray(rawRows)) {
    rawRows.forEach((row) => {
      const country = (row["Country"] || "").trim();
      const rawLocation = (row["Branch"] || "").trim();
      const officerName = (row["Officer_Name"] || "").trim();
      const role = (row["Role"] || "Branch Officer").trim();
      const displayLocation = country ? `${rawLocation}, ${country}` : rawLocation;
      const key = `${country}__${rawLocation}`;
      if (!groupedMap[key]) {
        groupedMap[key] = { flag: getCountryFlag(country), location: displayLocation, officers: [] };
      }
      if (officerName) {
        groupedMap[key].officers.push({ name: officerName, role });
      }
    });
  }

  const branches = Object.values(groupedMap);
  const toggleBranch = (location: string) => {
    setActiveBranch((prev) => (prev === location ? null : location));
  };

  return (
    <PageLayout
      eyebrow="Leadership & Branches"
      title={<>Real people.<br /><em>Real accountability.</em></>}
      subtitle=""
    >
      <div style={S.orgContainer}>
        {/* Tier 1: CEO */}
        <div style={S.orgTier}>
          <div style={S.tierLabel}>Chief Executive Officer — Level 1</div>
          <div style={S.ceoCenter}>
            <div style={{ ...S.leaderCard, ...S.ceoCard, minWidth: isMobile ? "100%" : 240 }}>
              <span style={S.pn}>Nidhi Patel</span>
              <span style={S.pr}>Chief Executive Officer</span>
            </div>
          </div>
        </div>

        {/* Tier 2: C-Suite */}
        <div style={S.orgTier}>
          <div style={S.tierLabel}>C-Suite Executives — Level 2</div>
          <div style={{
            ...S.cSuiteGrid,
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
          }}>
            {cSuite.map((c) => (
              <div key={c.name} style={{ ...S.leaderCard, ...S.csuiteCard }}>
                <span style={{ ...S.pn, fontSize: isMobile ? 13 : 15 }}>{c.name}</span>
                <span style={{ ...S.pr, fontSize: isMobile ? 11 : 12 }}>{c.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tier 3: Branches */}
        <div style={S.orgTier}>
          <div style={S.sectionLabel}>
            <span style={S.labelLine} />
            <span style={S.labelText}>BRANCHES (CLICK TO EXPAND)</span>
            <span style={S.labelLine} />
          </div>

          {loading ? (
            <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "12px 0", textAlign: "center" }}>
              Loading global branches...
            </div>
          ) : branches.length === 0 ? (
            <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "12px 0", textAlign: "center" }}>
              No branch records found.
            </div>
          ) : (
            <div style={S.branchStack}>
              {branches.map((b) => {
                const isExpanded = activeBranch === b.location;
                return (
                  <div key={b.location} style={S.branchBlock}>
                    <div
                      style={{ ...S.branchHeader, cursor: "pointer" }}
                      onClick={() => toggleBranch(b.location)}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8 }}>
                        <div style={{ fontSize: isMobile ? 12 : 13 }}>
                          <span style={{ marginRight: 8 }}>{b.flag}</span>
                          {b.location}
                        </div>
                        <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {isExpanded ? "▲ Hide" : `▼ Show (${b.officers.length})`}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{
                        ...S.branchGrid,
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
                      }}>
                        {b.officers.map((officer) => (
                          <div key={officer.name} style={S.branchOfficer}>
                            <span style={S.pn}>{officer.name}</span>
                            <span style={S.pr}>{officer.role}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Leadership Profiles */}
      <div style={{ marginTop: 48 }}>
        <h2 style={{ ...S.bioSectionTitle, fontSize: isMobile ? 18 : 22 }}>Leadership &amp; Mentor Profiles</h2>
        
        <div style={{ ...S.bioCard, ...S.bioGold, padding: isMobile ? "16px" : 24 }}>
          <div style={{ ...S.mentorBadge, ...S.goldBadge }}>
            <span style={{ marginRight: 6 }}>⭐</span> Founding Mentor &amp; Seed Investor
          </div>
          <div style={{ ...S.bioCardHeader, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 16 }}>
            {/* Fallback/Avatar emoji placeholder */}
            <img style={S.bioAvatar} src={dhronocharyaImg} alt="User Avatar" />
            
            {/* Flex wrapper pushing the high-res layout image directly to the right end */}
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", gap: 16 }}>
              <div>
                <h4 style={{ ...S.profileName, fontSize: isMobile ? 16 : 18 }}>Dr. Chittranjan Patel Bhai</h4>
                <span style={{ ...S.profileSub, color: "var(--gold-light)" }}>Lead Mentor &amp; Seed Investor</span>
              </div>
              
              {/* High-res photograph pulled to the right end with updated sizing and offset alignment */}
              {chittranjanImg && (
                <img style={S.profilePhoto} src={chittranjanImg} alt="Dr. Chittranjan Patel Bhai Profile" />
              )}
            </div>
          </div>
          <ul style={S.bioBullets}>
            <li style={S.bioBullet}>World-class specialist in technology venture investing and early-stage capital allocation</li>
            <li style={S.bioBullet}>Seed investor in <strong>NVIDIA (NVDA)</strong> and <strong>Micron Technology (MU)</strong> — identified both before they became household names</li>
            <li style={S.bioBullet}>Multi-million dollar portfolio mentor whose conviction shaped SSN's investment philosophy</li>
          </ul>
        </div>

        {[
          {
            badge: "🎯 1st Key Strategist, CEO & Sponsor",
            name: "Nidhi Patel",
            role: "Chief Executive Officer",
            avatar: "👩‍💼",
            image: nidhiImg,
            bullets: [
              <><strong>15+ years</strong> in Financial Services and Operations</>,
              <>First key strategist who helped architect the SSN fund structure, governance model, and member protection framework</>,
              <>Options Settlement, Trade Processing, Operational Risk, Compliance, and Portfolio Rebalancing at institutional scale</>,
            ],
            clients: ["FedEx", "Express Scripts", "Ecolab", "Ritchie Bros.", "RR Donnelley", "ProAg"],
          },
          {
            badge: "🎯 1st Key Strategist, CIO & Sponsor",
            name: "Kaivalya Patel",
            role: "Chief Investment Officer",
            avatar: "👨‍💼",
            image: kaivalyaImg, 
            bullets: [
              <>Capital Allocation • Investment Policy • Governance • Change Management — enterprise scale</>,
              <><strong>15+ years</strong> as partner in Hospitality business — Choice Hotels, Pennsylvania</>,
              <>Branch Development and investor relations across USA and India</>,
              <>Deep Fortune 500 network spanning financial services, technology, and retail</>,
            ],
            clients: ["CME Group", "Fidelity", "Wells Fargo", "Capital One", "AmEx", "JPMorgan", "UnitedHealth", "Dell", "Thomson Reuters"],
          },
        ].map((profile) => (
          <div key={profile.name} style={{ ...S.bioCard, ...S.bioTeal, padding: isMobile ? "16px" : 24 }}>
            <div style={{ ...S.mentorBadge, ...S.tealBadge }}>{profile.badge}</div>
            <div style={{ ...S.bioCardHeader, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 10 : 16 }}>
              <div style={S.bioAvatar}>{profile.avatar}</div>
              
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", gap: 16 }}>
                <div>
                  <h4 style={{ ...S.profileName, fontSize: isMobile ? 16 : 18 }}>{profile.name}</h4>
                  <span style={{ ...S.profileSub, color: "var(--teal-light)" }}>{profile.role}</span>
                </div>
                {profile.image && (
                  <img
                    style={{...S.profilePhoto, objectFit: 'cover', objectPosition: 'center 5%'}}
                    src={profile.image}
                    alt={`${profile.name} Profile`}
                  />
                )}
              </div>

            </div>
            <ul style={S.bioBullets}>
              {profile.bullets.map((b, i) => <li key={i} style={S.bioBullet}>{b}</li>)}
            </ul>
            <div style={S.bioClientLabel}>Representative Client Experience</div>
            <div style={S.bioClients}>
              {profile.clients.map((c) => <span key={c} style={S.bioClientChip}>{c}</span>)}
            </div>
          </div>
        ))}
      </div>
    </PageLayout>
  );
}

const S: Record<string, React.CSSProperties> = {
  orgContainer: { display: "flex", flexDirection: "column", gap: 32, marginTop: 12 },
  orgTier: { display: "flex", flexDirection: "column", gap: 12 },
  tierLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", fontWeight: 600 },
  sectionLabel: { display: "flex", alignItems: "center", gap: 16, marginTop: 8, marginBottom: 8 },
  labelLine: { flex: 1, height: 1, background: "rgba(255,255,255,0.07)" },
  labelText: { fontSize: 10, letterSpacing: "0.2em", color: "var(--text-muted)", fontWeight: 500, whiteSpace: "nowrap" },
  ceoCenter: { display: "flex", justifyContent: "center" },
  cSuiteGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 },
  leaderCard: { padding: "16px 20px", borderRadius: 10, display: "flex", flexDirection: "column", gap: 4, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" },
  ceoCard: { background: "rgba(29, 158, 117, 0.08)", border: "1px solid rgba(29, 158, 117, 0.25)", textAlign: "center" },
  csuiteCard: { textAlign: "center" },
  pn: { fontSize: 15, fontWeight: 700, color: "var(--text-primary)" },
  pr: { fontSize: 12, color: "var(--text-secondary)" },
  branchStack: { display: "flex", flexDirection: "column", gap: 16 },
  branchBlock: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 12, overflow: "hidden" },
  branchHeader: { background: "rgba(255,255,255,0.03)", padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.05)", display: "flex", width: "100%", boxSizing: "border-box" },
  branchGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 1, background: "rgba(255,255,255,0.05)" },
  branchOfficer: { background: "var(--dark-bg, #0b0f17)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 2 },
  bioSectionTitle: { fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 20 },
  bioCard: { padding: 24, borderRadius: 12, marginBottom: 20, position: "relative" },
  bioGold: { background: "rgba(255, 176, 79, 0.02)", border: "1px solid rgba(255, 176, 79, 0.12)" },
  bioTeal: { background: "rgba(29, 158, 117, 0.02)", border: "1px solid rgba(29, 158, 117, 0.1)" },
  mentorBadge: { display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, marginBottom: 16 },
  goldBadge: { background: "rgba(255, 176, 79, 0.1)", color: "var(--gold-light)" },
  tealBadge: { background: "rgba(29, 158, 117, 0.1)", color: "var(--teal-light)" },
  bioCardHeader: { display: "flex", gap: 16, alignItems: "center", marginBottom: 16 },
  bioAvatar: { width: 44, height: 44, borderRadius: "50%", background: "rgba(255,255,255,0.05)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 },
  
  // Added a negative top margin to lift the photo slightly upwards
  profilePhoto: { width: '100px', height: '100px', borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid rgba(255,255,255,0.15)", marginTop: -4 },
  
  profileName: { fontSize: 18, fontWeight: 600, color: "var(--text-primary)", margin: 0 },
  profileSub: { fontSize: 13, fontWeight: 500 },
  bioBullets: { margin: "0 0 20px 0", paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8 },
  bioBullet: { fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.5 },
  bioClientLabel: { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)", fontWeight: 600, marginBottom: 10 },
  bioClients: { display: "flex", flexWrap: "wrap", gap: 6 },
  bioClientChip: { padding: "4px 10px", borderRadius: 6, fontSize: 12, background: "rgba(255,255,255,0.05)", color: "var(--text-secondary)" },
};